import { uuidv7 } from "uuidv7";
import { z } from "zod";
import { BankCashItemType } from "@common/types/bankCashItem";
import {
    FinancialAccount,
    FinancialAccountType,
} from "@common/types/financialAccount";
import { cashItemTypeOf } from "./cashItemAmount";

const cashItemImporterRowSchema = z.object({
    _id: z
        .string()
        .min(1)
        .catch(() => uuidv7()),
    checkNumber: z.string().catch(""),
    date: z.string().catch(""),
    financialAccountId: z.number().int().nullable().catch(null),
    counterpartyId: z.string().uuid().nullable().catch(null),
    memo: z.string().catch(""),
    categoryId: z.string().uuid().nullable().catch(null),
    accountId: z.string().uuid().nullable().catch(null),
    accrualAccountId: z.string().uuid().nullable().catch(null),
    classIds: z.array(z.string()).nullable().catch(null),
    amount: z.number().finite().nullable().catch(null),
});

export type CashItemImporterRow = z.infer<typeof cashItemImporterRowSchema>;

export type CashItemImporterField = Exclude<keyof CashItemImporterRow, "_id">;

export const cashItemImporterFields = cashItemImporterRowSchema
    .keyof()
    .options.filter((field): field is CashItemImporterField => field !== "_id");

export function createCashItemImporterRow(
    fields: Partial<CashItemImporterRow> = {},
): CashItemImporterRow {
    return cashItemImporterRowSchema.parse(fields);
}

type CashItemBatchRowResult =
    | { itemIndex: number; success: true }
    | { itemIndex: number; success: false; error: { message: string } };

export interface CashItemImportFailure {
    rowId: string;
    rowNumber: number;
    message: string;
}

export function applyCashItemBatchResults<
    Row extends Pick<CashItemImporterRow, "_id">,
>(params: {
    rows: readonly Row[];
    entries: ReadonlyArray<{ rowId: string }>;
    results: readonly CashItemBatchRowResult[];
}): { remainingRows: Row[]; failures: CashItemImportFailure[] } {
    const savedRowIds = new Set<string>();
    const messagesByRowId = new Map<string, string>();
    for (const result of params.results) {
        const rowId = params.entries[result.itemIndex]?.rowId;
        if (rowId === undefined) {
            continue;
        }
        if (result.success) {
            savedRowIds.add(rowId);
        } else {
            messagesByRowId.set(rowId, result.error.message);
        }
    }

    const remainingRows = params.rows.filter(
        (row) => !savedRowIds.has(row._id),
    );
    const failures = remainingRows.flatMap((row, index) => {
        const message = messagesByRowId.get(row._id);
        return message === undefined
            ? []
            : [{ rowId: row._id, rowNumber: index + 1, message }];
    });
    return { remainingRows, failures };
}

export function isBlankCashItemRow(row: CashItemImporterRow): boolean {
    return (
        row.checkNumber.trim() === "" &&
        !/\d/.test(row.date) &&
        row.counterpartyId === null &&
        row.memo.trim() === "" &&
        row.categoryId === null &&
        row.accountId === null &&
        row.accrualAccountId === null &&
        (row.classIds ?? []).length === 0 &&
        (row.amount ?? 0) === 0
    );
}

interface WithLedgerAccountsClearedParams {
    row: CashItemImporterRow;
    previousRow: CashItemImporterRow;
    financialAccountsById: ReadonlyMap<
        number,
        Pick<FinancialAccount, "entityId">
    >;
}

export function withLedgerAccountsClearedOnEntityChange({
    row,
    previousRow,
    financialAccountsById,
}: WithLedgerAccountsClearedParams): CashItemImporterRow {
    const entityIdOf = (financialAccountId: number | null) =>
        financialAccountId === null
            ? null
            : (financialAccountsById.get(financialAccountId)?.entityId ?? null);
    if (
        entityIdOf(row.financialAccountId) ===
        entityIdOf(previousRow.financialAccountId)
    ) {
        return row;
    }
    return {
        ...row,
        accountId:
            row.accountId === previousRow.accountId ? null : row.accountId,
        accrualAccountId:
            row.accrualAccountId === previousRow.accrualAccountId
                ? null
                : row.accrualAccountId,
    };
}

interface WithNonCheckingAccountClearedParams {
    row: CashItemImporterRow;
    previousRow: CashItemImporterRow;
    financialAccountsById: ReadonlyMap<
        number,
        Pick<FinancialAccount, "financialType">
    >;
}

export function withNonCheckingAccountClearedWhenAmountBecomesCheck({
    row,
    previousRow,
    financialAccountsById,
}: WithNonCheckingAccountClearedParams): CashItemImporterRow {
    const selectedAccount =
        row.financialAccountId === null ||
        row.financialAccountId !== previousRow.financialAccountId
            ? undefined
            : financialAccountsById.get(row.financialAccountId);
    if (
        cashItemTypeOf(previousRow.amount) === BankCashItemType.CHECK ||
        cashItemTypeOf(row.amount) !== BankCashItemType.CHECK ||
        selectedAccount === undefined ||
        selectedAccount.financialType === FinancialAccountType.CHECKING
    ) {
        return row;
    }
    return {
        ...row,
        financialAccountId: null,
        accountId:
            row.accountId === previousRow.accountId ? null : row.accountId,
        accrualAccountId:
            row.accrualAccountId === previousRow.accrualAccountId
                ? null
                : row.accrualAccountId,
    };
}
